import { useState } from 'react';
import { ChevronDown, ChevronRight, Check, X } from 'lucide-react';
import type { CarmRecord } from '../types';

interface TariffResultsTableProps {
  data: CarmRecord[];
  totalCount: number | null;
  isLoading?: boolean;
}

const TREATMENT_CODES: Record<string, { name: string; description: string }> = {
  '002': { name: 'BPT', description: 'British Preferential Tariff' },
  '003': { name: 'General', description: 'No trade agreement (35% — sanctioned countries only)' },
  '007': { name: 'MFN', description: 'Most Favoured Nation — default rate for WTO countries (China, etc.)' },
  '009': { name: 'AUT', description: 'Australia Free Trade Agreement' },
  '010': { name: 'NZT', description: 'New Zealand FTA' },
  '011': { name: 'GPT', description: 'General Preferential Tariff — reduced rates for developing countries' },
  '013': { name: 'MXT', description: 'Mexico (CUSMA)' },
  '014': { name: 'UST', description: 'United States (CUSMA) — most US goods enter free' },
  '021': { name: 'CPTPP', description: 'Trans-Pacific Partnership (Japan, Vietnam, Australia, etc.)' },
  '022': { name: 'CPTPP-A', description: 'CPTPP Australia-specific' },
  '023': { name: 'CPTPP-B', description: 'CPTPP Brunei-specific' },
  '024': { name: 'CPTPP-C', description: 'CPTPP Chile-specific' },
  '025': { name: 'CEUT', description: 'CETA — European Union FTA' },
  '026': { name: 'CETT', description: 'CETA transitional provisions' },
  '027': { name: 'CT', description: 'Chile FTA (older bilateral)' },
  '028': { name: 'CHFTA', description: 'Canada-Honduras FTA' },
  '029': { name: 'CKFTA', description: 'Canada-Korea FTA' },
  '030': { name: 'CPFTA', description: 'Canada-Panama FTA' },
  '031': { name: 'UKFTA', description: 'Canada-United Kingdom FTA' },
  '032': { name: 'UKTQ', description: 'UK Tariff Quota' },
  '033': { name: 'CUFTA', description: 'Canada-Ukraine FTA' },
  '034': { name: 'CUFTQ', description: 'Canada-Ukraine Tariff Quota' },
};

function getTreatmentLabel(code: string): string {
  const t = TREATMENT_CODES[code];
  return t ? `${t.name} (${code})` : `Treatment ${code}`;
}

function formatDate(val: unknown): string {
  if (!val || typeof val !== 'string') return '--';
  try {
    const d = new Date(val);
    if (d.getFullYear() > 9000) return 'No expiry';
    return d.toLocaleDateString('en-CA');
  } catch { return '--'; }
}

function DutyBadge({ duties }: { duties: CarmRecord[] }) {
  if (!duties || duties.length === 0) return <span className="text-muted-foreground">--</span>;

  // Find MFN rate (treatment 007 or 013)
  const mfn = duties.find((d) => d.TariffTreatmentCode === '007' || d.TariffTreatmentCode === '013');
  // Find CUSMA/UST rate (treatment 014)
  const cusma = duties.find((d) => d.TariffTreatmentCode === '014');

  const formatRate = (d: CarmRecord | undefined, label: string) => {
    if (!d) return null;
    const isFree = d.FreeQualifierIndicator === 'X' || d.FreeQualifierIndicator === true;
    const adValorem = Number(d.AdValoremRateRegValue) || 0;
    return (
      <span
        key={label}
        className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
          isFree
            ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
            : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
        }`}
      >
        {label}: {isFree ? 'FREE' : `${adValorem}%`}
      </span>
    );
  };

  return (
    <div className="flex gap-1 flex-wrap">
      {formatRate(mfn, 'MFN')}
      {formatRate(cusma, 'CUSMA')}
    </div>
  );
}

function BoolIcon({ value }: { value: unknown }) {
  if (value === true) return <Check className="h-3.5 w-3.5 text-green-600" />;
  if (value === false) return <X className="h-3.5 w-3.5 text-muted-foreground/40" />;
  return <span className="text-muted-foreground">--</span>;
}

export function TariffResultsTable({ data, totalCount, isLoading }: TariffResultsTableProps) {
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  if (isLoading) {
    return (
      <div className="border rounded-lg p-8 text-center text-muted-foreground">
        Searching CBSA tariff database...
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="border rounded-lg p-8 text-center text-muted-foreground">
        No results found. Try a broader search term or different tariff number.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        {data.length} result{data.length !== 1 ? 's' : ''}
        {totalCount && totalCount > data.length ? ` of ${totalCount.toLocaleString()} total` : ''}
      </p>

      <div className="border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/40">
                <th className="text-left px-3 py-2 font-medium w-8"></th>
                <th className="text-left px-3 py-2 font-medium">Tariff Number</th>
                <th className="text-left px-3 py-2 font-medium">Description</th>
                <th className="text-left px-3 py-2 font-medium">UOM</th>
                <th className="text-left px-3 py-2 font-medium">Duty Rates</th>
                <th className="text-center px-3 py-2 font-medium">GST</th>
                <th className="text-center px-3 py-2 font-medium">Permit</th>
                <th className="text-left px-3 py-2 font-medium">Valid</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => {
                const duties = (row.to_customsDuties as { results?: CarmRecord[] })?.results || [];
                const isExpanded = expandedRow === i;

                return (
                  <>
                    <tr
                      key={i}
                      className="border-b last:border-0 hover:bg-muted/30 cursor-pointer"
                      onClick={() => setExpandedRow(isExpanded ? null : i)}
                    >
                      <td className="px-3 py-2.5">
                        {isExpanded
                          ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                          : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                        }
                      </td>
                      <td className="px-3 py-2.5 font-mono font-medium whitespace-nowrap">
                        {String(row.TariffNumber || '')}
                      </td>
                      <td className="px-3 py-2.5 max-w-md">
                        <span className="line-clamp-2">{String(row.Description || '')}</span>
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">
                        {String(row.UnitOfMeasureCode || '--')}
                      </td>
                      <td className="px-3 py-2.5">
                        <DutyBadge duties={duties} />
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <BoolIcon value={row.GSTIndicator} />
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <BoolIcon value={row.PermitRequiredIndicator} />
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">
                        {formatDate(row.TariffNumberValidStartDate)}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr key={`${i}-detail`}>
                        <td colSpan={8} className="px-6 py-4 bg-muted/20 border-b">
                          <div className="space-y-3">
                            {/* Full description */}
                            <div>
                              <p className="text-[10px] font-medium text-muted-foreground uppercase mb-1">Full Description</p>
                              <p className="text-xs">{String(row.Description || '')}</p>
                            </div>

                            {/* Details grid */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                              <div>
                                <p className="text-[10px] font-medium text-muted-foreground uppercase">Tariff Item</p>
                                <p className="font-mono">{String(row.TariffItemNumber || '--')}</p>
                              </div>
                              <div>
                                <p className="text-[10px] font-medium text-muted-foreground uppercase">Special Classification</p>
                                <p>{String(row.SpecialClassificationIndicator || '--')}</p>
                              </div>
                              <div>
                                <p className="text-[10px] font-medium text-muted-foreground uppercase">Valid From</p>
                                <p>{formatDate(row.TariffNumberValidStartDate)}</p>
                              </div>
                              <div>
                                <p className="text-[10px] font-medium text-muted-foreground uppercase">Valid Until</p>
                                <p>{formatDate(row.TariffNumberValidEndDate)}</p>
                              </div>
                            </div>

                            {/* Duty rates table */}
                            {duties.length > 0 && (
                              <div>
                                <p className="text-[10px] font-medium text-muted-foreground uppercase mb-1">
                                  Duty Rates by Trade Agreement ({duties.length} treatments)
                                  <a href="#treatment-codes" className="ml-2 text-primary underline font-normal normal-case">What are these?</a>
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1">
                                  {duties
                                    .sort((a, b) => {
                                      // Sort: MFN first, then UST, then alphabetical
                                      const order = ['007', '014', '013', '025', '021', '003'];
                                      const ai = order.indexOf(String(a.TariffTreatmentCode));
                                      const bi = order.indexOf(String(b.TariffTreatmentCode));
                                      if (ai !== -1 && bi !== -1) return ai - bi;
                                      if (ai !== -1) return -1;
                                      if (bi !== -1) return 1;
                                      return String(a.TariffTreatmentCode).localeCompare(String(b.TariffTreatmentCode));
                                    })
                                    .map((d, di) => {
                                    const isFree = d.FreeQualifierIndicator === 'X' || d.FreeQualifierIndicator === true;
                                    const adValorem = Number(d.AdValoremRateRegValue) || 0;
                                    const code = String(d.TariffTreatmentCode);
                                    const treatment = TREATMENT_CODES[code];
                                    const isKey = code === '007' || code === '014' || code === '013';
                                    return (
                                      <div
                                        key={di}
                                        className={`px-2 py-1.5 rounded text-[10px] ${
                                          isFree
                                            ? 'bg-green-50 dark:bg-green-950/20'
                                            : 'bg-amber-50 dark:bg-amber-950/20'
                                        } ${isKey ? 'ring-1 ring-primary/30' : ''}`}
                                        title={treatment?.description || ''}
                                      >
                                        <span className="font-medium">{getTreatmentLabel(code)}</span>
                                        {' — '}
                                        <span className={isFree ? 'text-green-700 dark:text-green-300 font-medium' : 'text-amber-700 dark:text-amber-300 font-medium'}>
                                          {isFree ? 'FREE' : `${adValorem}%`}
                                        </span>
                                        {treatment && (
                                          <span className="block text-muted-foreground mt-0.5">{treatment.description}</span>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
